import { useFormik } from "formik";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom'

import { useJobAlertUpdateMutation, useGetJobAlertQuery } from "../api/jobAlertApiSlice";
import { useGetSectorsQuery } from "../../JobPost/api/jobCreateApiSlice"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import * as Yup from 'yup'
import { LgbtToast } from "../../../components/LgbtToast/LgbtToast";
import { useSelector } from "react-redux";


function useJobAlertUpdateForm() {

    const { jobAlertId } = useParams();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [AlertResponseMessage, setAlertResponseMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitAlert, { isFetching, isLoading, isError, Error }] = useJobAlertUpdateMutation();
    const { data: jobAlertData, isFetching: isJobAlertFetching, isLoading: isJobAlertLoading, isError: isJobAlertError } = useGetJobAlertQuery(jobAlertId);
    const { data: sectorTypes } = useGetSectorsQuery();
    const auth = useSelector((state) => state.auth || {})
    const formRef = useRef();
    const navigate = useNavigate();

    const initialValues = {
        full_name: jobAlertData?.full_name || auth.user.name || auth.user.username || '',
        job_title_keyword: jobAlertData?.job_title_keyword || '',
        email: jobAlertData?.email || auth.user.email || '',
        location: jobAlertData?.location || '',
        work_from_home: jobAlertData?.work_from_home || false,
        salary_amount: jobAlertData?.minimum_salary || 0,
        minimum_salary: jobAlertData?.minimum_salary || 0,
        maximum_salary: jobAlertData?.maximum_salary || 0,
        contract_type: jobAlertData?.contract_type || [],
        sector: jobAlertData?.sector || [],
        alert_frequency: jobAlertData?.alert_frequency == 'Daily' ? 1 : 2,
        distance: jobAlertData?.distance || 0,
    };

    const validationSchema = Yup.object({
        'full_name': Yup.string(),
        'job_title_keyword': Yup.string().required('Job title is required'),
        'email': Yup.string().email('Invalid email format').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Email must include a domain with a dot').required('Email is required'),
        'location': Yup.string(),
        'work_from_home': Yup.bool(),
        'contract_type': Yup.array(),
        'salary_amount': Yup.number(),
        'minimum_salary': Yup.number(),
        'maximum_salary': Yup.number(),
        'sector': Yup.array(),
        'alert_frequency': Yup.number(),
        'distance': Yup.number(),
    })

    const jobAlertUpdateForm = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setStatus, resetForm, setFieldError }) => {
            setIsSubmitting(true);
            try {
                if (!executeRecaptcha) {
                    LgbtToast({ message: 'reCAPTCHA not available. Please refresh the page.', type: 'error' });
                    setAlertResponseMessage('reCAPTCHA error');
                    setIsSubmitting(false);
                    return;
                }

                const recaptchaToken = await executeRecaptcha("alert_form_submission");

                let submissionData = {
                    ...values,
                    recaptcha: recaptchaToken,
                    minimum_salary: values.salary_amount
                };

                if (submissionData.minimum_salary > submissionData.maximum_salary) {
                    LgbtToast({ message: "Minimum salary should be less than maximum salary.", type: 'error' });
                    return;
                }

                submissionData.alert_frequency = submissionData.alert_frequency == 1 ? 'Daily' : 'Weekly';

                const response = await submitAlert({ body: submissionData, jobAlertId: jobAlertData.id });

                if (response.error) {
                    LgbtToast({ message: response.error.status, type: 'error' });
                    setAlertResponseMessage('Job Alert not updated');
                } else {
                    LgbtToast({ message: 'Job Alert updated successfully', type: 'success' });

                    if (auth.isAuthenticated)
                        navigate('/my-account/job-alerts');
                    else
                        setAlertResponseMessage('Job Alert updated successfully');

                    resetForm();
                }

                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                LgbtToast({ message: 'An error occurred', type: 'error' });
                setAlertResponseMessage('Job Alert not updated');
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    useEffect(() => {
        if (Object.keys(auth.user).length <= 0 && jobAlertUpdateForm) return;

        jobAlertUpdateForm.setValues({
            full_name: auth.user.name || auth.user.username || '',
            email: auth.user.email || ''
        });

    }, [auth.user, formRef, jobAlertData, isJobAlertLoading, isJobAlertFetching])

    useEffect(() => {
        if (jobAlertData) {
            if (!formRef.current) {
                formRef.current = true;
                jobAlertUpdateForm.setValues({
                    full_name: auth.user.name || auth.user.username || '',
                    email: auth.user.email || '',
                    job_title_keyword: jobAlertData.job_title_keyword || '',
                    location: jobAlertData.location || '',
                    work_from_home: jobAlertData.work_from_home || false,
                    salary_amount: jobAlertData.minimum_salary || 0,
                    minimum_salary: jobAlertData.minimum_salary || 0,
                    maximum_salary: jobAlertData.maximum_salary || 0,
                    contract_type: jobAlertData.contract_type || [],
                    sector: jobAlertData.sector,
                    alert_frequency: jobAlertData.alert_frequency,
                    distance: jobAlertData.distance || 0,
                });
            }
        }

    }, [jobAlertData]);

    return { jobAlertUpdateForm, isFetching, isLoading, isError, Error, AlertResponseMessage, jobAlertData, isSubmitting };
}

export default useJobAlertUpdateForm;
